import { LoadingPage } from '@chiroup/components/LoadingPage';
import { ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { MassTextApprovalQueueItem } from '@chiroup/core/types/MassText.type';
import React, { use } from 'react';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import {
  useBulkCampaignApprovals,
  useDecideBulkCampaignApproval,
} from '../../hooks/useBulkCampaignApprovals';
import Header from '../layout/Header';

const thClass = 'px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-dark-gray-100';
const tdClass = 'px-3 py-3 text-sm text-gray-700 dark:text-dark-gray-300 align-top';

const when = (value: number | null): string =>
  (value
    ? ChiroUpDayJsCommon.display.datetime(value, ChiroUpDayJsCommon.defaultTimezone)
    : undefined) || '—';

/**
 * Clinics on ChiroUp's standard (Low Volume Mixed) 10DLC registration get one
 * self-service bulk text campaign per rolling 24 hours. A second one lands
 * here. Approving lifts ONLY that rule: the brand's carrier daily cap, the
 * per-clinic daily SMS-part ceiling and the per-minute throughput budget are
 * all still enforced when the campaign runs.
 */
const BulkCampaignApprovals: React.FC = () => {
  const { createToast } = use(ToastContext);
  const { hasPermission, isLoading: permissionsLoading } = useAdminPermissions();
  const canDecide = hasPermission('campaign.approve_bulk_sms');

  const { data, isFetching } = useBulkCampaignApprovals(canDecide);
  const decideMutation = useDecideBulkCampaignApproval();

  const decide = async (row: MassTextApprovalQueueItem, approve: boolean) => {
    const note =
      window.prompt(
        approve
          ? `Approve "${row.campaignName}" for an earlier send? Optional note:`
          : `Deny "${row.campaignName}"? Optional note the clinic will see:`,
        ''
      ) ?? undefined;

    // A blank string is a valid decision with no note; Cancel (null) aborts.
    if (note === undefined) return;

    try {
      await decideMutation.mutateAsync({
        clinicId: row.clinicId,
        campaignId: row.campaignId,
        decision: { decision: approve ? 'approve' : 'deny', note },
      });
      createToast({
        title: approve ? 'Campaign approved' : 'Campaign denied',
        description: approve
          ? 'The campaign is scheduled for immediate pickup. Carrier throughput and daily part limits still apply.'
          : 'The campaign was returned to draft with your note.',
        type: ToastTypes.Success,
        duration: 5000,
      });
    } catch {
      createToast({
        title: 'Error',
        description: 'The decision could not be recorded. Reload the queue and try again.',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }
  };

  if (permissionsLoading) return <LoadingPage />;

  if (!canDecide) {
    return (
      <>
        <Header title="Bulk Campaign Approvals" />
        <p className="p-6 text-sm text-gray-600 dark:text-dark-gray-300">
          You do not have permission to review bulk campaign approvals.
        </p>
      </>
    );
  }

  const rows = data?.data || [];

  return (
    <>
      <Header title="Bulk Campaign Approvals" />
      <div className="p-6">
        <p className="mb-4 max-w-3xl text-sm text-gray-600 dark:text-dark-gray-300">
          Clinics on standard 10DLC registration get one self-service bulk text campaign per rolling
          24 hours. Approving here lifts only that limit — the brand&apos;s carrier daily cap, the
          per-clinic daily SMS-part ceiling and the per-minute throughput budget still apply, so an
          approved campaign can still be paced across days. A clinic that regularly needs more
          same-day volume needs upgraded carrier registration, not an approval.
        </p>

        {isFetching && !rows.length ? (
          <LoadingPage />
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-dark-gray-700">
              <thead>
                <tr>
                  <th className={thClass}>Clinic</th>
                  <th className={thClass}>Campaign</th>
                  <th className={thClass}>Recipients</th>
                  <th className={thClass}>Requested</th>
                  <th className={thClass}>Previous send</th>
                  <th className={thClass}>Unrestricted from</th>
                  <th className={thClass}>Decision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-gray-800">
                {rows.map(row => (
                  <tr key={`${row.clinicId}-${row.campaignId}`}>
                    <td className={tdClass}>
                      {row.clinicName || '—'}
                      <div className="text-xs text-gray-500 dark:text-dark-gray-400">
                        #{row.clinicId}
                      </div>
                    </td>
                    <td className={tdClass}>
                      {row.campaignName}
                      <div className="text-xs text-gray-500 dark:text-dark-gray-400">
                        {row.messageType} · {row.deliveryMode}
                      </div>
                    </td>
                    <td className={tdClass}>{row.totalRecipients.toLocaleString()}</td>
                    <td className={tdClass}>
                      {when(row.approvalRequestedAt)}
                      <div className="text-xs text-gray-500 dark:text-dark-gray-400">
                        {row.approvalRequestedByName || row.approvalRequestedBy || '—'}
                      </div>
                    </td>
                    <td className={tdClass}>{when(row.previousSendStartedAt)}</td>
                    <td className={tdClass}>{when(row.earliestUnrestrictedAt)}</td>
                    <td className={tdClass}>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => decide(row, true)}
                          disabled={decideMutation.isLoading}
                          className="rounded bg-primary-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => decide(row, false)}
                          disabled={decideMutation.isLoading}
                          className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200 disabled:opacity-50"
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600 dark:text-dark-gray-300">
            No bulk campaigns are awaiting approval.
          </p>
        )}
      </div>
    </>
  );
};

export default BulkCampaignApprovals;
